import type { BaseAIAction } from '@ben/columbia/components/assistant/actions/base';
import { getAvailableAIActions } from '@ben/columbia/components/assistant/actions/helpers';
import { CloseButton } from '@ben/columbia/components/assistant/close';
import Icon, { IconName, IconScale } from '@ben/orion/icon';
import { STATIC_URL } from '@ben/shared/constants';
import { useDisclosure } from '@mantine/hooks';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css, useTheme } from 'styled-components';
import { useEventEmitter } from './emitter';
import AssistantPanel from './panel';
import AssistantPreview from './preview';
import { FollowUpType } from './types';

const BenLogo = `${STATIC_URL}/images/ben-marque.svg`;

const AssistantWidget = () => {
  const [disabled, disabledHandlers] = useDisclosure();
  const [isOpen, openHandlers] = useDisclosure();
  const [aiActions, setAiActions] = useState<BaseAIAction[] | undefined>();
  const [selectedAction, setSelectedAction] = useState<
    BaseAIAction | undefined
  >();
  const [showDisable, showDisableHandlers] = useDisclosure();
  const [currentComponent, setCurrentComponent] = useState<string>('');
  const theme = useTheme();
  const portal = document.querySelector('#portal-assistant');

  useEventEmitter({
    eventName: 'tool.usage',
    handler: (data) => {
      if (!data || !('currentValue' in data)) {
        return;
      }
      setCurrentComponent(data.componentKey);

      let additionalValue: unknown;
      if ('additionalValue' in data) {
        additionalValue = data.additionalValue;
      }

      setAiActions(
        getAvailableAIActions(
          data.toolName,
          data.currentValue,
          data.onChangeValue,
          additionalValue
        )
      );
    },
  });

  useEventEmitter({
    eventName: 'tool.dismiss',
    handler: (data) => {
      if (!data || !('componentKey' in data)) {
        return;
      }

      // if the tool is not the current tool or the assistant is open, do nothing
      if (data.componentKey !== currentComponent) {
        return;
      }

      setAiActions([]);
      setCurrentComponent('');
    },
    dependencies: [currentComponent, isOpen],
  });

  if (!portal) {
    return;
  }

  if (disabled) {
    return;
  }

  const processAction = (
    action: [FollowUpType, unknown],
    option: BaseAIAction
  ) => {
    let newOption = option;

    switch (action[0]) {
      case FollowUpType.ChatMessage: {
        if (!newOption.chatMessages) {
          newOption.chatMessages = [];
        }

        if (!Array.isArray(action[1])) {
          newOption.chatMessages.push({
            message: action[1] as string,
            isUser: false,
          });
          break;
        }
        newOption.chatMessages.push({
          message: (action[1] as string[])[0],
          background: (action[1] as string[])[1],
          isUser: false,
        });
        break;
      }
      case FollowUpType.UpdateInput: {
        if (!newOption?.externalSetValue) {
          break;
        }
        newOption.externalSetValue(action[1] as string);
        break;
      }
      case FollowUpType.SwitchOption: {
        const overrideOption = action[1] as BaseAIAction;
        openHandlers.open();
        if (overrideOption.firstMessage) {
          if (!newOption.chatMessages) {
            newOption.chatMessages = [];
          }
          newOption.chatMessages.push({
            message: newOption.firstMessage ?? '',
            isUser: false,
          });
        }
        overrideOption.chatMessages = newOption.chatMessages;
        newOption = overrideOption;
        break;
      }
      case FollowUpType.ContinueConversation: {
        newOption.args = (action[1] as unknown[]) ?? [];
        break;
      }
      case FollowUpType.SetExecuted: {
        newOption.executed = true;
        break;
      }
      case FollowUpType.SetTerminal: {
        newOption.requiresUserPrompt = false;
        break;
      }
      default: {
        break;
      }
    }
    return newOption;
  };

  const triggerAction = async (option: BaseAIAction) => {
    const actions = await option?.trigger();
    for (const action of actions) {
      option = processAction(action, option);
    }

    setSelectedAction(option);
  };

  const activateOption = (option: BaseAIAction) => {
    openHandlers.open();

    // if the option has a first message show it:
    if (option.firstMessage && option.chatMessages === undefined) {
      option.chatMessages = [
        {
          message: option.firstMessage,
          isUser: false,
        },
      ];
    }

    setSelectedAction(option);
  };

  const resetPanel = () => {
    openHandlers.close();
    setAiActions([]);
  };

  return createPortal(
    <AssistantParent>
      <AssistantContainer
        onMouseEnter={() => showDisableHandlers.open()}
        onMouseLeave={() => showDisableHandlers.close()}
      >
        <img src={BenLogo} alt={'Benny'} />
      </AssistantContainer>
      {showDisable && (
        <CloseButton
          onClick={() => disabledHandlers.open()}
          onMouseEnter={() => showDisableHandlers.open()}
        >
          <Icon
            name={IconName.Close}
            color={theme.palette.neutral[50]}
            scale={IconScale.S}
          />
        </CloseButton>
      )}

      {isOpen && selectedAction ? (
        <AssistantPanel
          resetOptions={resetPanel}
          selectedAiAction={selectedAction}
          onTriggerAction={triggerAction}
        />
      ) : (
        <AssistantPreview
          aiActions={aiActions}
          onSelectOption={activateOption}
          resetOptions={resetPanel}
        />
      )}
    </AssistantParent>,
    portal
  );
};

export default AssistantWidget;

const AssistantParent = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  width: 64px;
  height: 64px;
`;
const AssistantContainer = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.palette.offWhite};
    color: #fff;
    border: none;
    padding: 10px;
    border-radius: ${theme.borderRadius.full};
    width: 100%;
    height: 100%;
  `};
`;
